import type { ChromelessPlayer, PresentationMode } from "theoplayer";

/**
 * プレイヤーの種類 (THEOplayer / IVS) の違いを吸収して、
 * ボタン側から同じ操作でネイティブ Picture-in-Picture を扱うためのインターフェース。
 */
export type PictureInPictureController = {
  /** この端末・このプレイヤーで Picture-in-Picture を開始できるか */
  isSupported: () => boolean;
  /** 今 Picture-in-Picture で再生されているか */
  isActive: () => boolean;
  /**
   * Picture-in-Picture の開始と終了を切り替える。
   * ユーザー操作を起点にしないとブラウザに拒否されるため、クリックハンドラから呼ぶこと。
   */
  toggle: () => void;
  /** 状態の変化を購読する。戻り値の関数で購読を解除する */
  subscribe: (listener: () => void) => () => void;
};

/* ------------------------------------------------------------------ */
/* THEOplayer                                                          */
/* ------------------------------------------------------------------ */

/**
 * THEOplayer 8.9.0 で Presentation API に追加された、ブラウザネイティブの
 * (ウィンドウ外に浮く) Picture-in-Picture。
 * 'picture-in-picture' はページ内で浮く THEOplayer 独自の PiP なので別物。
 * Firefox は PiP の Web API を持たないため supportsMode が false になる。
 * @see https://optiview.dolby.com/docs/theoplayer/v8/api-reference/web/types/PresentationMode.html
 */
const NATIVE_PICTURE_IN_PICTURE: PresentationMode = "native-picture-in-picture";
const INLINE: PresentationMode = "inline";

export type TheoPlayerForPictureInPicture = Pick<
  ChromelessPlayer,
  | "presentation"
  | "element"
  | "currentTime"
  | "addEventListener"
  | "removeEventListener"
>;

/**
 * supportsMode('native-picture-in-picture') はソースが読み込まれるまで false を返し、
 * `player.source = ...` の直後に非同期で発火する sourcechange で true に変わる。
 * portal はプレイヤー生成後にソースを差し込む (ライブは認証後) ので、
 * 生成時の判定だけだとボタンが隠れたままになる。
 */
const THEO_PLAYER_SYNC_EVENTS = ["sourcechange", "loadedmetadata"] as const;

/**
 * THEOplayer は video 要素の metadata 読み込みを待たずに requestPictureInPicture() を呼ぶため、
 * ソース設定直後や差し替え直後 (readyState が 0 の間、1 秒前後) にクリックすると
 * ブラウザに InvalidStateError で拒否され、何も起きずに終わる (8.14 で実測)。
 * その間のクリックは video 要素の loadedmetadata を待ってから要求する。
 * THEOplayer 側の loadedmetadata イベントは video 要素より先に発火するので、DOM のイベントを見る。
 * ブラウザがユーザー操作を有効とみなす期間 (Chrome は 5 秒) 内に収めるため、待ちすぎたら諦める。
 */
const DEFERRED_REQUEST_TIMEOUT_MS = 4000;

const hasVideoMetadata = (playerEl: HTMLElement): boolean =>
  Array.from(playerEl.querySelectorAll("video")).some(
    (video) => video.readyState >= HTMLMediaElement.HAVE_METADATA
  );

export const createTheoPictureInPictureController = (
  player: TheoPlayerForPictureInPicture
): PictureInPictureController => {
  const { presentation } = player;
  const isActive = () => presentation.currentMode === NATIVE_PICTURE_IN_PICTURE;

  const requestMode = (mode: PresentationMode) => {
    presentation.requestMode(mode);
  };

  let cancelDeferredRequest: (() => void) | null = null;

  const deferRequestUntilMetadata = () => {
    const handleLoadedMetadata = (event: Event) => {
      if (!(event.target instanceof HTMLVideoElement)) return;
      cancel();
      requestMode(NATIVE_PICTURE_IN_PICTURE);
    };
    const timeoutId = window.setTimeout(
      () => cancel(),
      DEFERRED_REQUEST_TIMEOUT_MS
    );
    const cancel = () => {
      player.element.removeEventListener(
        "loadedmetadata",
        handleLoadedMetadata,
        true
      );
      window.clearTimeout(timeoutId);
      cancelDeferredRequest = null;
    };
    player.element.addEventListener(
      "loadedmetadata",
      handleLoadedMetadata,
      true
    );
    cancelDeferredRequest = cancel;
  };

  return {
    isSupported: () => presentation.supportsMode(NATIVE_PICTURE_IN_PICTURE),
    isActive,
    toggle: () => {
      if (cancelDeferredRequest) {
        // 待っている間にもう一度押されたら取り消す
        cancelDeferredRequest();
        return;
      }
      if (isActive()) {
        requestMode(INLINE);
        return;
      }
      if (!hasVideoMetadata(player.element)) {
        deferRequestUntilMetadata();
        return;
      }
      requestMode(NATIVE_PICTURE_IN_PICTURE);
    },
    subscribe: (listener) => {
      presentation.addEventListener("presentationmodechange", listener);
      player.addEventListener(THEO_PLAYER_SYNC_EVENTS, listener);
      return () => {
        presentation.removeEventListener("presentationmodechange", listener);
        player.removeEventListener(THEO_PLAYER_SYNC_EVENTS, listener);
      };
    },
  };
};

/**
 * ネイティブ PiP 小窓のシークバーは THEOplayer を介さず `<video>.currentTime` を直接書き換える。
 * THEOplayer 8.14 はこの外部シークにセグメント読み込みで追従せず、
 * `seeking` が発火したまま `seeked` が来ない (m3u8 / セグメントの fetch が始まらない) 状態で止まる。
 * `player.currentTime` に同じ値を入れ直して THEOplayer 自身のシーク経路に乗せると復帰するので、
 * ネイティブ PiP 中に限って video 要素の `seeking` を捕捉して再シークする。
 * 自分の再シークでも `seeking` が発火するため、直前に要求した位置と一致するものは無視してループを防ぐ。
 *
 * 戻り値の関数で解除する。
 */
export const attachNativePictureInPictureSeekWorkaround = (
  player: TheoPlayerForPictureInPicture
): (() => void) => {
  let requestedSeekTime: number | null = null;

  const handleSeeking = (event: Event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    if (player.presentation.currentMode !== NATIVE_PICTURE_IN_PICTURE) return;

    const time = video.currentTime;
    if (
      requestedSeekTime !== null &&
      Math.abs(time - requestedSeekTime) < 0.05
    ) {
      requestedSeekTime = null;
      return;
    }

    requestedSeekTime = time;
    player.currentTime = time;
  };

  // video 要素が作り直されても拾えるよう、プレイヤー要素の capture phase で受ける
  player.element.addEventListener("seeking", handleSeeking, true);
  return () => {
    player.element.removeEventListener("seeking", handleSeeking, true);
  };
};

/* ------------------------------------------------------------------ */
/* video 要素 (IVS プレイヤー用)                                         */
/* ------------------------------------------------------------------ */

/**
 * iOS Safari は標準の Picture-in-Picture API を実装しておらず、webkit 独自の
 * presentation mode API だけを持つ。
 * @see https://developer.apple.com/documentation/webkitjs/htmlvideoelement/1631913-webkitsetpresentationmode
 */
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLVideoElement {
    webkitPresentationMode?: string;
    webkitSupportsPresentationMode?: (mode: string) => boolean;
    webkitSetPresentationMode?: (mode: string) => void;
  }
}

const WEBKIT_PICTURE_IN_PICTURE = "picture-in-picture";
const WEBKIT_INLINE = "inline";

/**
 * 標準の Picture-in-Picture API が使えるか。
 * Firefox は API 自体を持たないため false になる。
 */
const canUseStandardApi = (video: HTMLVideoElement): boolean =>
  "pictureInPictureEnabled" in document &&
  document.pictureInPictureEnabled &&
  !video.disablePictureInPicture;

/**
 * webkit 独自の presentation mode API で Picture-in-Picture が使えるか。
 * disablePictureInPicture は標準 API と同じく尊重する。
 */
const canUseWebkitApi = (video: HTMLVideoElement): boolean =>
  !video.disablePictureInPicture &&
  video.webkitSupportsPresentationMode?.(WEBKIT_PICTURE_IN_PICTURE) === true;

/**
 * この端末・この video 要素で Picture-in-Picture を開始できるか。
 */
const canUsePictureInPicture = (video: HTMLVideoElement | null): boolean => {
  if (!video) {
    return false;
  }

  if (canUseStandardApi(video)) {
    return true;
  }

  return canUseWebkitApi(video);
};

/**
 * この video 要素が今 Picture-in-Picture で再生されているか。
 */
const isInPictureInPicture = (video: HTMLVideoElement | null): boolean => {
  if (!video) {
    return false;
  }

  return (
    document.pictureInPictureElement === video ||
    video.webkitPresentationMode === WEBKIT_PICTURE_IN_PICTURE
  );
};

/**
 * Picture-in-Picture の開始と終了を切り替える。
 * ユーザー操作を起点にしないとブラウザに拒否されるため、クリックハンドラから呼ぶこと。
 */
const togglePictureInPicture = (video: HTMLVideoElement): void => {
  // 終了は「どちらの API で入ったか」に合わせる。
  // macOS Safari は両方の API を持つので、webkit 側で入った PiP を標準 API で二重に要求しないようにする
  if (document.pictureInPictureElement === video) {
    void document.exitPictureInPicture();
    return;
  }
  if (video.webkitPresentationMode === WEBKIT_PICTURE_IN_PICTURE) {
    video.webkitSetPresentationMode?.(WEBKIT_INLINE);
    return;
  }

  if (canUseStandardApi(video)) {
    void video.requestPictureInPicture();
    return;
  }
  if (canUseWebkitApi(video)) {
    video.webkitSetPresentationMode?.(WEBKIT_PICTURE_IN_PICTURE);
  }
};

/**
 * 状態の変化を拾うイベント。
 * webkitpresentationmodechanged は iOS Safari の PiP 状態変化。
 * いずれも video 要素で発火するので、video 要素が作り直されても拾えるよう
 * プレイヤー要素の capture phase で受ける。
 */
const VIDEO_ELEMENT_SYNC_EVENTS = [
  "loadstart",
  "loadedmetadata",
  "enterpictureinpicture",
  "leavepictureinpicture",
  "webkitpresentationmodechanged",
];

export const createVideoElementPictureInPictureController = (
  playerEl: Element | null
): PictureInPictureController => {
  const getVideo = () =>
    playerEl?.querySelector<HTMLVideoElement>("video.vjs-tech") ?? null;

  return {
    isSupported: () => canUsePictureInPicture(getVideo()),
    isActive: () => isInPictureInPicture(getVideo()),
    toggle: () => {
      const video = getVideo();
      if (video) {
        togglePictureInPicture(video);
      }
    },
    subscribe: (listener) => {
      VIDEO_ELEMENT_SYNC_EVENTS.forEach((eventName) => {
        playerEl?.addEventListener(eventName, listener, true);
      });
      return () => {
        VIDEO_ELEMENT_SYNC_EVENTS.forEach((eventName) => {
          playerEl?.removeEventListener(eventName, listener, true);
        });
      };
    },
  };
};
